import React, { useMemo } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Sparkles, CreditCard } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Inline banner shown on the Dashboard while the user is in their 7-day
 * free trial. Communicates:
 *  - Days remaining (or "ends today" / "ends tomorrow")
 *  - Whether they still owe a card to keep access
 *  - A direct CTA to /app/pricing to add a card
 *
 * Hidden in any state other than `subscription_status === 'trialing'`.
 */
export default function TrialBanner({ sub }) {
  const navigate = useNavigate();

  const data = useMemo(() => {
    if (!sub) return null;
    if (sub.subscription_status !== 'trialing') return null;
    const endStr = sub.trial_end;
    if (!endStr) return null;
    const end = new Date(endStr);
    const now = new Date();
    const ms = end - now;
    if (ms <= 0) return null;
    const days = Math.ceil(ms / (1000 * 60 * 60 * 24));
    let label;
    if (days === 1) label = 'ends tomorrow';
    else if (days <= 0) label = 'ends today';
    else label = `${days} days left`;
    return { days, label, end };
  }, [sub]);

  if (!data) return null;
  const needsCard = !sub?.has_payment_method;

  return (
    <Card className="border-[#004C54]/30 bg-gradient-to-br from-[#004C54]/8 to-[#4FBCC7]/8" data-testid="trial-banner">
      <CardContent className="p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#004C54] text-white flex items-center justify-center">
          <Sparkles className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-mono-accent uppercase tracking-[0.18em] text-[10px] font-bold text-[#004C54]">
            Free trial · {data.label}
          </div>
          <div className="font-heading text-base sm:text-lg font-bold leading-snug mt-0.5">
            {needsCard
              ? 'Add a card before your trial ends to keep going.'
              : 'You\'re all set. Card on file — no interruption when the trial ends.'}
          </div>
          {needsCard && (
            <div className="text-sm text-foreground/65 mt-0.5 leading-snug">
              No card on file yet. We won't charge during the trial; you'll downgrade to free if you don't add one.
            </div>
          )}
        </div>
        {needsCard && (
          <Button
            onClick={() => navigate('/app/pricing')}
            className="bg-[#004C54] hover:bg-[#003a40] text-white font-semibold w-full sm:w-auto"
            data-testid="trial-banner-add-card"
          >
            <CreditCard className="h-4 w-4 mr-2" /> Add a card
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
